import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { List, Typography, Button, Space, Avatar, Empty, Card, Tooltip, Badge, theme } from 'antd';
import { CheckOutlined, UserOutlined, BellOutlined, MessageOutlined, FileTextOutlined, PushpinOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { notificationsApi } from '../../api/client';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from 'react-router-dom';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

export default function NotificationsPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { token } = theme.useToken();

    const { data: notifications = [], isLoading } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => notificationsApi.getAll().then((res: any) => res.data),
    });

    const markAsReadMutation = useMutation({
        mutationFn: (id: string) => notificationsApi.markAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
        },
    });

    const markAllAsReadMutation = useMutation({
        mutationFn: () => notificationsApi.markAllAsRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
        },
    });

    const handleItemClick = (item: any) => {
        if (!item.isRead) {
            markAsReadMutation.mutate(item.id);
        }
        if (item.link) {
            navigate(item.link);
        }
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'MENTION': return <MessageOutlined style={{ color: '#1890ff' }} />;
            case 'ASSIGNMENT': return <PushpinOutlined style={{ color: '#faad14' }} />;
            case 'UPDATE': return <FileTextOutlined style={{ color: '#52c41a' }} />;
            case 'REMINDER': return <ClockCircleOutlined style={{ color: '#fa541c' }} />;
            default: return <BellOutlined />;
        }
    };

    return (
        <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={4} style={{ margin: 0 }}>
                    <BellOutlined style={{ marginRight: 8 }} />
                    Wszystkie powiadomienia
                </Title>

                {notifications.some((n: any) => !n.isRead) && (
                    <Button
                        icon={<CheckOutlined />}
                        onClick={() => markAllAsReadMutation.mutate()}
                        loading={markAllAsReadMutation.isPending}
                    >
                        Oznacz wszystkie jako przeczytane
                    </Button>
                )}
            </div>

            <List
                loading={isLoading}
                itemLayout="horizontal"
                dataSource={notifications}
                locale={{ emptyText: <Empty description="Brak powiadomień" /> }}
                renderItem={(item: any) => (
                    <List.Item
                        actions={[
                            !item.isRead && (
                                <Tooltip title="Oznacz jako przeczytane">
                                    <Button
                                        type="text"
                                        icon={<CheckOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            markAsReadMutation.mutate(item.id);
                                        }}
                                    />
                                </Tooltip>
                            )
                        ]}
                        style={{
                            cursor: 'pointer',
                            background: item.isRead ? 'transparent' : token.colorSuccessBg,
                            padding: '16px',
                            borderRadius: '4px',
                            marginBottom: '8px',
                            border: `1px solid ${token.colorBorderSecondary}`
                        }}
                        onClick={() => handleItemClick(item)}
                    >
                        <List.Item.Meta
                            avatar={
                                item.triggeredBy ? (
                                    <Avatar src={item.triggeredBy.avatar} style={{ backgroundColor: '#1890ff' }}>
                                        {item.triggeredBy.firstName?.[0]}
                                    </Avatar>
                                ) : (
                                    <Avatar icon={<UserOutlined />} />
                                )
                            }
                            title={
                                <Space>
                                    {getIcon(item.type)}
                                    <Text strong={!item.isRead}>{item.title}</Text>
                                    {!item.isRead && <Badge status="processing" />}
                                </Space>
                            }
                            description={
                                <div>
                                    <div style={{ marginBottom: 4 }}>{item.message}</div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')} ({dayjs(item.createdAt).fromNow()})
                                    </Text>
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />
        </Card>
    );
}
